import { useMemo } from 'react'
import {
  useGetTransactions,
  TransactionTypes,
  RangeDate,
} from '@/domain/transactions'

interface UseUserTransactionsParams {
  userId: string
  searchValue?: string
  selectedTypes?: TransactionTypes[]
  dateRange?: RangeDate
  enabled?: boolean
}

export const useUserTransactions = ({
  userId,
  searchValue = '',
  selectedTypes = [],
  dateRange,
  enabled = true,
}: UseUserTransactionsParams) => {
  // Prepare query parameters for transactions API
  const queryParams = useMemo(() => {
    const isSearching = !!searchValue && searchValue.trim().length > 0

    const params: any = {
      participant_id: userId, // Filter by specific user
      restrict_to_participants: true, // Only show transactions with this user
      search: searchValue || undefined,
      types: selectedTypes.length > 0 ? selectedTypes : undefined,
      // When searching, ignore date range to search across all time
      // Only apply date filters when not searching
      from_date:
        dateRange?.startDate && !isSearching ? dateRange.startDate : undefined,
      to_date:
        dateRange?.endDate && !isSearching ? dateRange.endDate : undefined,
      page_size: 20,
    }

    // Remove undefined values
    return Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined),
    )
  }, [userId, searchValue, selectedTypes, dateRange])

  // Use the existing transactions hook with user-specific parameters
  const transactionsQuery = useGetTransactions({
    ...queryParams,
    enabled: enabled && !!userId,
  })

  // Flatten all pages of transactions
  const transactions = useMemo(() => {
    if (!transactionsQuery.data?.pages) return []

    return transactionsQuery.data.pages.flatMap(
      (page) => page.transactions || [],
    )
  }, [transactionsQuery.data?.pages])

  // Check if both queries are empty
  const isEmpty = transactions.length === 0

  // Loading states
  const isLoading = transactionsQuery.isLoading
  const isRefetching = transactionsQuery.isRefetching
  const isError = transactionsQuery.isError

  // Refetch function
  const refetch = async () => {
    await transactionsQuery.refetch()
  }

  return {
    transactions,
    transactionsQuery,
    isLoading,
    isRefetching,
    isError,
    isEmpty,
    refetch,
  }
}
